import { useAuth } from '../context/AuthContext';

/**
 * Custom hook for permission checking
 * Provides utilities to check user permissions throughout the application
 */
export const usePermissions = () => {
  const { user } = useAuth();

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission key to check
   * @returns {boolean} Whether user has the permission
   */
  const hasPermission = (permission) => {
    if (!user) return false;
    
    // Admin users have all permissions
    if (user.role === 'Admin') return true;
    
    // Check user permissions object
    if (user.permissions && typeof user.permissions === 'object') {
      return user.permissions[permission] === true;
    }
    
    // Fallback to userPermissions if available (from backend)
    if (user.userPermissions && typeof user.userPermissions === 'object') {
      return user.userPermissions[permission] === true;
    }
    
    return false;
  };

  /**
   * Check if user has any of the specified permissions
   * @param {string[]} permissions - Array of permission keys to check
   * @returns {boolean} Whether user has at least one of the permissions
   */
  const hasAnyPermission = (permissions) => {
    if (!Array.isArray(permissions)) return false;
    return permissions.some(permission => hasPermission(permission));
  };

  /**
   * Check if user has all of the specified permissions
   * @param {string[]} permissions - Array of permission keys to check
   * @returns {boolean} Whether user has all of the permissions
   */
  const hasAllPermissions = (permissions) => {
    if (!Array.isArray(permissions)) return false;
    return permissions.every(permission => hasPermission(permission));
  };

  /**
   * Check if user has a specific role
   * @param {string} role - Role to check
   * @returns {boolean} Whether user has the role
   */
  const hasRole = (role) => {
    return user?.role === role;
  };

  /**
   * Check if user has any of the specified roles
   * @param {string[]} roles - Array of roles to check
   * @returns {boolean} Whether user has at least one of the roles
   */
  const hasAnyRole = (roles) => {
    if (!Array.isArray(roles)) return false;
    return roles.includes(user?.role);
  };

  /**
   * Check if user is admin
   * @returns {boolean} Whether user is admin
   */
  const isAdmin = () => {
    return user?.role === 'Admin';
  };

  /**
   * Get all user permissions
   * @returns {object} User permissions object
   */
  const getAllPermissions = () => {
    if (!user) return {};
    
    // Admin users have all permissions (we could fetch all available permissions here)
    if (user.role === 'Admin') {
      return { '*': true }; // Wildcard to indicate all permissions
    }
    
    return user.permissions || user.userPermissions || {};
  };

  /**
   * Check if user can modify another user (for user management)
   * @param {object} targetUser - User to check modification rights for
   * @returns {boolean} Whether current user can modify the target user
   */
  const canModifyUser = (targetUser) => {
    if (!user || !targetUser) return false;
    
    // Only admins can modify users
    if (user.role !== 'Admin') return false;
    
    // Admins can modify non-admin users
    // Admins can modify other admins (but this could be restricted if needed)
    return true;
  };

  /**
   * Check if user can access a specific feature/page
   * @param {string} feature - Feature identifier
   * @returns {boolean} Whether user can access the feature
   */
  const canAccessFeature = (feature) => {
    const featurePermissions = {
      'user-management': ['admin.users.view'],
      'patient-management': ['patients.view'],
      'clinical-data': ['clinical.problems.view', 'clinical.allergies.view'],
      'appointments': ['appointments.view'],
      'reports': ['reports.view'],
      'lab-management': ['admin.lab.templates'],
      'documents': ['documents.view'],
      'flowsheet': ['clinical.flowsheet.view'],
      'health-metrics': ['admin.health.metrics']
    };

    const requiredPermissions = featurePermissions[feature];
    if (!requiredPermissions) return true; // No restrictions defined

    const hasAccess = hasAnyPermission(requiredPermissions);

    // Debug logging for health-metrics feature
    if (feature === 'health-metrics') {
      console.log('🔍 Health Metrics Feature Check:', {
        feature,
        requiredPermissions,
        hasAccess,
        userPermissions: user?.permissions,
        userRole: user?.role
      });
    }

    return hasAccess;
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    isAdmin,
    getAllPermissions,
    canModifyUser,
    canAccessFeature,
    user
  };
};
